import {React, useContext} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {
  MoleculeDrawer,
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import MoleculeModal, {MoleculeModalContent, MoleculeModalFooter, MoleculeModalSizes} from '@s-ui/react-molecule-modal'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationAutoClose,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconEditRounded from '@adv-mt/mt-svg-icons/lib/EditRounded.js'
import IconPeriodForwardOutline from '@adv-mt/mt-svg-icons/lib/PeriodForwardOutline.js'

import ModalDeleteAlertAction from './action/index.js'

const IconCloseModal = () => (
  <AtomIcon as="button" size={ATOM_ICON_SIZES.medium}>
    <IconClose />
  </AtomIcon>
)
const EMPTY_VALUE = ''

const ModalDeleteAlert = ({
  allowFiltersUpdate = true,
  currentUserAlert,
  isOpen = false,
  onChangeFrequency,
  onClose,
  onConfirm,
  onUpdate
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = domain.get('isMobile')
  const {FREQUENCY_NOTIFICATION_DAILY, FREQUENCY_NOTIFICATION_REALTIME} = config.get('alerts')

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }
  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const {frequencyNotification, numOfFiltersSelected} = currentUserAlert

  const frequencyValue = {
    [FREQUENCY_NOTIFICATION_REALTIME]: i18n.t('SEARCH_SAVE_FREQUENCY_SELECTION.REAL_TIME', 1).toLowerCase(),
    [FREQUENCY_NOTIFICATION_DAILY]: i18n.t('SEARCH_SAVE_FREQUENCY_SELECTION.DAILY', 1).toLowerCase()
  }

  const showUpdateFrequencyAction = Boolean(frequencyNotification)

  const content = (
    <MoleculeModalContent>
      <div className="mt-modalDeleteAlert-header">
        <AtomButton
          design={atomButtonDesigns.FLAT}
          leftIcon={<IconCloseModal />}
          onClick={() => onClose()}
          shape={atomButtonShapes.CIRCULAR}
        />
      </div>
      <h4 className="mt-modalDeleteAlert-title">{i18n.t('SEARCH.DELETE_SEARCH_TITLE')}</h4>
      {(allowFiltersUpdate || showUpdateFrequencyAction) && (
        <p className="mt-modalDeleteAlert-subtitle">{i18n.t('SEARCH.DELETE_SEARCH_SUBTITLE')}</p>
      )}
      <div className="mt-modalDeleteAlert-wrapper">
        {allowFiltersUpdate && (
          <ModalDeleteAlertAction
            icon={IconEditRounded}
            text={i18n.t('SEARCH.SELECTED_FILTERS', {
              smart_count: numOfFiltersSelected || EMPTY_VALUE
            })}
            onItemClick={onUpdate}
          />
        )}
        {showUpdateFrequencyAction && (
          <ModalDeleteAlertAction
            icon={IconPeriodForwardOutline}
            text={i18n.t('SEARCH_SAVE_FREQUENCY_SELECTION.FREQUENCY', {
              frequency: frequencyValue[frequencyNotification] || EMPTY_VALUE
            })}
            onItemClick={onChangeFrequency}
          ></ModalDeleteAlertAction>
        )}
      </div>
      <div className="mt-modalDeleteAlert-notification">
        <MoleculeNotification
          alignItems={moleculeNotificationAlignItems.FLEX_START}
          autoClose={moleculeNotificationAutoClose.manual}
          effect={false}
          roundedCorners={moleculeNotificationBorderSizes.medium}
          showCloseButton={false}
          showLeftIconMobile={true}
          type={moleculeNotificationTypes.system}
          variation={moleculeNotificationVariations.positive}
        >
          {i18n.t('SEARCH.DEACTIVATE_CHANNELS_TEXT')}
        </MoleculeNotification>
      </div>
    </MoleculeModalContent>
  )

  const footer = (
    <MoleculeModalFooter>
      <div className="mt-modalDeleteAlert-footer">
        <AtomButton
          design={atomButtonDesigns.FLAT}
          fullWidth={!isHigherThanMobile}
          onClick={onClose}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.MEDIUM}
        >
          {i18n.t('COMMENTS.CANCEL')}
        </AtomButton>
        <AtomButton
          design={atomButtonDesigns.SOLID}
          fullWidth={!isHigherThanMobile}
          onClick={onConfirm}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.MEDIUM}
        >
          {i18n.t('SEARCH.DELETE_SEARCH_CONFIRM')}
        </AtomButton>
      </div>
    </MoleculeModalFooter>
  )

  return (
    <>
      {isHigherThanMobile ? (
        <MoleculeModal
          closeOnEscKeyDown
          closeOnOutsideClick
          fitContent
          isContentless
          isOpen={isOpen}
          onClose={onClose}
          size={MoleculeModalSizes.SMALL}
        >
          {content}
          {footer}
        </MoleculeModal>
      ) : (
        <>
          <MoleculeDrawerOverlay isVisible={isOpen} />
          <MoleculeDrawer
            closeOnOutsideClick
            isOpen={isOpen}
            onClose={onClose}
            placement={moleculeDrawerPlacements.BOTTOM}
            ref={{current: document.body}} // Pending fix in SUI Component
            size={moleculeDrawerSizes.AUTO}
          >
            {content}
            {footer}
          </MoleculeDrawer>
        </>
      )}
    </>
  )
}

ModalDeleteAlert.displayName = 'ModalDeleteAlert'
ModalDeleteAlert.propTypes = {
  allowFiltersUpdate: PropTypes.bool,
  currentUserAlert: PropTypes.object,
  isOpen: PropTypes.bool,
  onChangeFrequency: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUpdate: PropTypes.func
}

export default ModalDeleteAlert
